import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';
import {
  useInventoryParts,
  useSuppliers,
  usePurchaseOrders,
  useBookings,
  useCreateWarrantyClaim,
  type PartListItem,
  type SupplierListItem,
  type PurchaseOrderListItem,
  type BookingListItem,
  type WarrantyClaimType,
} from '@/hooks';
import { useNotify } from '@/stores/notificationStore';
import { eurosToCents } from '@/utils/money';

interface WarrantyClaimDialogProps {
  open: boolean;
  onClose: () => void;
}

const CLAIM_TYPES: WarrantyClaimType[] = ['defective', 'premature_failure', 'wrong_part'];
const CLAIM_TYPE_LABEL_KEY: Record<WarrantyClaimType, string> = {
  defective: 'warranty.typeDefective',
  premature_failure: 'warranty.typePrematureFailure',
  wrong_part: 'warranty.typeWrongPart',
};

/**
 * Create a warranty claim against a defective/prematurely-failed/wrong part
 * (#994, split from #229 osa 3). Independent of the RMA flow — doesn't
 * necessarily involve physically returning stock.
 */
export function WarrantyClaimDialog({ open, onClose }: WarrantyClaimDialogProps) {
  const { t } = useTranslation('inventory');
  const notify = useNotify();
  const { isDirty, markDirty } = useFormDirty(open);
  const createClaim = useCreateWarrantyClaim();

  const [part, setPart] = useState<PartListItem | null>(null);
  const [supplier, setSupplier] = useState<SupplierListItem | null>(null);
  const [claimType, setClaimType] = useState<WarrantyClaimType>('defective');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('1');
  // Raw typed text, not a formatted-and-reformatted derived value — avoids
  // the digit-scrambling bug already found in other price fields (#1068).
  const [claimAmountInput, setClaimAmountInput] = useState('');
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrderListItem | null>(null);
  const [workOrder, setWorkOrder] = useState<BookingListItem | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setPart(null);
      setSupplier(null);
      setClaimType('defective');
      setDescription('');
      setQuantity('1');
      setClaimAmountInput('');
      setPurchaseOrder(null);
      setWorkOrder(null);
      setError('');
    }
  }, [open]);

  const partsQuery = useInventoryParts({}, { enabled: open });
  const parts = useMemo(
    () => (partsQuery.data?.pages ?? []).flatMap((page) => page.data),
    [partsQuery.data],
  );

  const suppliersQuery = useSuppliers();
  const suppliers = useMemo(
    () => (suppliersQuery.data?.pages ?? []).flatMap((page) => page.data),
    [suppliersQuery.data],
  );

  const poQuery = usePurchaseOrders();
  const purchaseOrders = useMemo(
    () => (poQuery.data?.pages ?? []).flatMap((page) => page.data),
    [poQuery.data],
  );

  const bookingsQuery = useBookings();
  const bookings = useMemo(
    () => (bookingsQuery.data?.pages ?? []).flatMap((page) => page.data),
    [bookingsQuery.data],
  );

  const handleSubmit = () => {
    if (!part) {
      setError(t('warranty.partRequired'));
      return;
    }
    if (!supplier) {
      setError(t('warranty.supplierRequired'));
      return;
    }
    const quantityNum = parseInt(quantity, 10);
    if (isNaN(quantityNum) || quantityNum < 1) {
      setError(t('warranty.quantityInvalid'));
      return;
    }
    let claimAmountCents: number | undefined;
    if (claimAmountInput.trim() !== '') {
      const cents = eurosToCents(claimAmountInput);
      if (cents == null || cents < 0) {
        setError(t('warranty.claimAmountInvalid'));
        return;
      }
      claimAmountCents = cents;
    }

    createClaim.mutate(
      {
        partId: part.id,
        supplierId: supplier.id,
        purchaseOrderId: purchaseOrder?.id,
        workOrderId: workOrder?.id,
        claimType,
        description: description.trim() || undefined,
        quantity: quantityNum,
        claimAmountCents,
      },
      {
        onSuccess: () => {
          notify.success(t('warranty.createdSuccess'), t('warranty.createdSuccessDesc'));
          onClose();
        },
        onError: () => notify.error(t('warranty.createFailed')),
      },
    );
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('warranty.createClaim')}
      isDirty={isDirty}
      onSubmit={handleSubmit}
      submitLabel={t('warranty.createClaim')}
      isLoading={createClaim.isPending}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Autocomplete
              value={part}
              onChange={(_, newValue) => { setPart(newValue); markDirty(); if (error) setError(''); }}
              options={parts}
              getOptionLabel={(option) => `${option.sku} — ${option.name}`}
              loading={partsQuery.isLoading}
              renderInput={(params) => <TextField {...params} label={t('warranty.selectPart')} autoFocus />}
            />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel id="warranty-supplier-label">{t('warranty.selectSupplier')}</InputLabel>
              <Select
                labelId="warranty-supplier-label"
                label={t('warranty.selectSupplier')}
                value={supplier?.id ?? ''}
                onChange={(e) => {
                  setSupplier(suppliers.find((s) => s.id === e.target.value) ?? null);
                  markDirty();
                  if (error) setError('');
                }}
              >
                {suppliers.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel id="warranty-claim-type-label">{t('warranty.claimType')}</InputLabel>
              <Select
                labelId="warranty-claim-type-label"
                label={t('warranty.claimType')}
                value={claimType}
                onChange={(e) => { setClaimType(e.target.value as WarrantyClaimType); markDirty(); }}
              >
                {CLAIM_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{t(CLAIM_TYPE_LABEL_KEY[type])}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              type="number"
              label={t('purchaseOrderForm.colQuantity')}
              value={quantity}
              onChange={(e) => { setQuantity(e.target.value); markDirty(); if (error) setError(''); }}
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label={t('warranty.claimAmount')}
              value={claimAmountInput}
              onChange={(e) => { setClaimAmountInput(e.target.value); markDirty(); if (error) setError(''); }}
              slotProps={{ input: { endAdornment: '€' } }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label={t('warranty.description')}
              value={description}
              onChange={(e) => { setDescription(e.target.value); markDirty(); }}
            />
          </Grid>
          <Grid size={12}>
            <Autocomplete
              value={purchaseOrder}
              onChange={(_, newValue) => { setPurchaseOrder(newValue); markDirty(); }}
              options={purchaseOrders}
              getOptionLabel={(option) => `${option.orderNumber} — ${option.supplier.name}`}
              renderInput={(params) => <TextField {...params} label={t('warranty.linkPurchaseOrder')} />}
            />
          </Grid>
          <Grid size={12}>
            <Autocomplete
              value={workOrder}
              onChange={(_, newValue) => { setWorkOrder(newValue); markDirty(); }}
              options={bookings}
              getOptionLabel={(option) => `${option.vehicleRegNumber} — ${option.customerName}`}
              renderInput={(params) => <TextField {...params} label={t('warranty.linkWorkOrder')} />}
            />
          </Grid>
          {error && (
            <Grid size={12}>
              <Typography variant="body2" color="error">{error}</Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </FormDialog>
  );
}
